/**
 * Pulse — employee response form.
 *
 * Renders the questions of a survey and lets the caller submit their
 * answers in a single POST. The page is reachable for any role (an
 * employee fills it out; an HR admin can preview the same page).
 *
 * On submit success, redirects back to /pulse — the surveys list
 * picks up the ``has_responded=true`` annotation and the row no
 * longer appears in an employee's "to-do" list.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";

import {
  getSurvey,
  submitResponse,
  type PulseAnswerInput,
  type PulseSurvey,
} from "@/api/pulse";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/useToast";
import { pageTransition } from "@/lib/utils";

interface AnswerState {
  rating?: number;
  text?: string;
}

export function PulseRespondPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [survey, setSurvey] = useState<PulseSurvey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getSurvey(id)
      .then((s) => {
        if (!cancelled) setSurvey(s);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load this survey.");
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const update = (questionId: string, patch: AnswerState) =>
    setAnswers((prev) => ({ ...prev, [questionId]: { ...prev[questionId], ...patch } }));

  const allAnswered = useMemo(() => {
    if (!survey) return false;
    return survey.questions.every((q) => {
      const a = answers[q.id];
      if (!a) return false;
      if (q.question_type === "scale_1_5" || q.question_type === "scale_1_10") {
        return typeof a.rating === "number";
      }
      return Boolean(a.text && a.text.trim());
    });
  }, [survey, answers]);

  const handleSubmit = async () => {
    if (!survey || !id) return;
    if (!allAnswered) {
      addToast("error", "Please answer every question before submitting.");
      return;
    }
    const payload: PulseAnswerInput[] = survey.questions.map((q) => {
      const a = answers[q.id] || {};
      if (q.question_type === "scale_1_5" || q.question_type === "scale_1_10") {
        return { question_id: q.id, rating: a.rating };
      }
      return { question_id: q.id, text_answer: a.text };
    });
    setSubmitting(true);
    try {
      await submitResponse(id, payload);
      addToast("success", "Thanks for your feedback!");
      navigate("/pulse");
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not submit your response.";
      addToast("error", detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <Card className="border-danger/40 bg-danger/5">
        <p className="text-sm text-danger">{error}</p>
      </Card>
    );
  }
  if (!survey) {
    return <div className="h-40 rounded bg-border/30 animate-pulse" />;
  }

  return (
    <motion.div {...pageTransition} className="max-w-2xl">
      <PageHeader
        title={survey.title}
        subtitle={survey.description || undefined}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate("/pulse")}>
            <ChevronLeft className="h-3.5 w-3.5" />
            Back
          </Button>
        }
      />

      <div className="mb-4 flex items-center gap-2 text-xs text-text-muted">
        {survey.is_anonymous ? (
          <Badge variant="info">Anonymous · responses are not linked to you</Badge>
        ) : (
          <Badge variant="default">Identified · responses are linked to your profile</Badge>
        )}
      </div>

      <div className="space-y-3">
        {survey.questions.map((q, idx) => (
          <Card key={q.id} className="space-y-3">
            <p className="text-sm font-medium text-text-primary">
              {idx + 1}. {q.question_text}
            </p>
            {q.question_type === "scale_1_5" && (
              <ScaleButtons
                min={1}
                max={5}
                value={answers[q.id]?.rating}
                onChange={(v) => update(q.id, { rating: v })}
              />
            )}
            {q.question_type === "scale_1_10" && (
              <ScaleButtons
                min={0}
                max={10}
                value={answers[q.id]?.rating}
                onChange={(v) => update(q.id, { rating: v })}
                lowLabel="Not at all likely"
                highLabel="Extremely likely"
              />
            )}
            {q.question_type === "text" && (
              <textarea
                value={answers[q.id]?.text || ""}
                onChange={(e) => update(q.id, { text: e.target.value })}
                rows={3}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                placeholder="Your answer…"
              />
            )}
            {q.question_type === "multiple_choice" && q.options && (
              <div className="space-y-2">
                {q.options.map((opt) => {
                  const checked = answers[q.id]?.text === opt;
                  return (
                    <label
                      key={opt}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                        checked
                          ? "border-accent bg-accent/5"
                          : "border-border hover:border-border/80"
                      }`}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        checked={checked}
                        onChange={() => update(q.id, { text: opt })}
                        className="h-4 w-4 text-accent focus:ring-accent"
                      />
                      <span className="text-sm text-text-primary">{opt}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </Card>
        ))}
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <Button variant="ghost" onClick={() => navigate("/pulse")}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} isLoading={submitting} disabled={!allAnswered}>
          Submit response
        </Button>
      </div>
    </motion.div>
  );
}


function ScaleButtons({
  min,
  max,
  value,
  onChange,
  lowLabel,
  highLabel,
}: {
  min: number;
  max: number;
  value: number | undefined;
  onChange: (v: number) => void;
  lowLabel?: string;
  highLabel?: string;
}) {
  const buttons = [];
  for (let i = min; i <= max; i++) buttons.push(i);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {buttons.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            aria-pressed={value === n}
            className={`min-w-[2.5rem] rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
              value === n
                ? "border-accent bg-accent text-white"
                : "border-border bg-surface text-text-primary hover:border-accent/60"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      {(lowLabel || highLabel) && (
        <div className="flex justify-between text-[11px] text-text-muted px-1">
          <span>{lowLabel}</span>
          <span>{highLabel}</span>
        </div>
      )}
    </div>
  );
}
